import {byte, clamp, int, panic, unitValue} from "@opendaw/lib-std"
import {PPQN, ppqn} from "@opendaw/lib-dsp"
import {DrumLane} from "./DrumLane"
import {Euclid} from "./Euclid"
import {Rng} from "./Rng"

export type DrumEvent = {
    position: ppqn
    duration: ppqn
    pitch: byte
    velocity: unitValue
}

export type DrumOptions = {
    seed: int
    density: number
}

export type DrumVariant = "pattern" | "fill" | "drop" | "build"

export namespace DrumGenerator {
    export const pulsesOf = ({steps, pulses, locked}: DrumLane, density: number): int =>
        locked ? pulses : clamp(Math.round(pulses * density), 0, steps)

    export const patternOf = (lane: DrumLane, density: number): ReadonlyArray<boolean> =>
        Euclid.pattern(lane.steps, pulsesOf(lane, density), lane.rotation)

    export const generate = (lanes: ReadonlyArray<DrumLane>, {seed, density}: DrumOptions): ReadonlyArray<DrumEvent> => {
        const events: Array<DrumEvent> = []
        lanes.forEach((lane, laneIndex) => {
            if (lane.mute) {return}
            const rng = new Rng(seed + laneIndex * 977)
            const pattern = patternOf(lane, density)
            const accents = Euclid.pattern(lane.steps, clamp(lane.accents, 0, lane.steps), lane.rotation)
            const duration = Math.max(1, lane.resolution >> 1)
            const count = Math.ceil(DrumLane.CycleDuration / lane.resolution)
            for (let index = 0; index < count; index++) {
                const step = index % lane.steps
                if (!pattern[step]) {continue}
                if (rng.next() >= lane.probability) {continue}
                const jitter = lane.humanize === 0 ? 0 : Math.round(rng.bipolar() * lane.humanize)
                const position = clamp(index * lane.resolution + jitter, 0, DrumLane.CycleDuration - 1)
                events.push({
                    position,
                    duration,
                    pitch: lane.note,
                    velocity: accents[step] ? lane.accentVelocity : lane.velocity
                })
            }
        })
        return events.sort((left, right) => left.position - right.position)
    }

    // AcidKit pads that have no lane of their own.
    const HiTom: byte = 45
    const MidTom: byte = 43
    const LowTom: byte = 41

    // One bar that closes a phrase: the kick holds the first half and drops out, a snare or clap rolls in
    // from eighths to sixteenths and rises, toms may walk down the last beat, an open hat ends it.
    export const fill = (lanes: ReadonlyArray<DrumLane>, {seed}: DrumOptions): ReadonlyArray<DrumEvent> => {
        const rng = new Rng(seed + 31337)
        const step = PPQN.SemiQuaver
        const duration = step >> 1
        const noteOf = (name: string): byte =>
            lanes.find(lane => lane.name === name)?.note ?? panic(`No lane '${name}'`)
        const events: Array<DrumEvent> = []
        const hit = (index: int, pitch: byte, velocity: unitValue): unknown =>
            events.push({position: index * step, duration, pitch, velocity})
        hit(0, noteOf("Kick"), 1.0)
        hit(4, noteOf("Kick"), 1.0)
        const roll = noteOf(rng.next() < 0.5 ? "Snare" : "Clap")
        for (let index = 0; index < 16; index++) {
            if (index < 8 && index % 2 !== 0) {continue}
            hit(index, roll, 0.4 + 0.6 * index / 15)
        }
        if (rng.next() < 0.6) {
            [HiTom, MidTom, LowTom, LowTom].forEach((pitch, offset) => hit(12 + offset, pitch, 0.8))
        }
        hit(14, noteOf("Open Hat"), 0.8)
        return events.sort((left, right) => left.position - right.position)
    }

    const Heavy: ReadonlyArray<string> = ["Kick", "Snare", "Clap"]

    // Every variant is the full four-bar cycle, so they all hand over on the same grid. A break lives in the
    // LAST bar of its variant: fill rolls, drop pulls the heavy drums, build pulls them a bar early and rolls.
    export const variant = (kind: DrumVariant, lanes: ReadonlyArray<DrumLane>,
                            options: DrumOptions): ReadonlyArray<DrumEvent> => {
        const pattern = generate(lanes, options)
        const heavy = lanes.filter(lane => Heavy.includes(lane.name)).map(lane => lane.note)
        const bar = PPQN.Bar
        const before = (bars: int): ReadonlyArray<DrumEvent> => pattern.filter(event => event.position < bars * bar)
        const thin = (events: ReadonlyArray<DrumEvent>, from: int, to: int): ReadonlyArray<DrumEvent> =>
            events.filter(event => event.position < from * bar || event.position >= to * bar
                || !heavy.includes(event.pitch))
        const shifted = (bars: int): ReadonlyArray<DrumEvent> =>
            fill(lanes, options).map(event => ({...event, position: event.position + bars * bar}))
        switch (kind) {
            case "pattern": return pattern
            case "fill": return before(3).concat(shifted(3))
            case "drop": return thin(pattern, 3, 4)
            case "build": return thin(before(3), 2, 3).concat(shifted(3))
        }
    }
}
